import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';

interface FAQ {
  question: string;
  answer: string;
}

interface Feature {
  icon: string;
  title: string;
  description: string;
}

interface Testimonial {
  quote: string;
  author: string;
  position: string;
  company: string;
}

@Component({
  selector: 'app-competitive-edge',
  standalone: true,
  imports: [CommonModule, RouterModule],
  template: `
    <!-- Hero Section -->
    <section class="relative bg-gradient-to-br from-gray-900 to-blue-900 text-white py-16 md:py-24">
      <div class="absolute inset-0 bg-pattern opacity-20"></div>
      <div class="container mx-auto px-4 relative z-10">
        <div class="flex flex-col md:flex-row items-center">
          <div class="md:w-1/2 mb-10 md:mb-0">
            <div class="inline-flex items-center space-x-2 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-sm text-sm font-medium mb-6">
              <span class="w-2 h-2 rounded-full bg-purple-400 animate-pulse"></span>
              <span>Competitive Intelligence</span>
            </div>
            <h1 class="text-3xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              Competitive Edge
            </h1>
            <p class="text-xl md:text-2xl mb-8 text-blue-100">
              Gain strategic advantage with AI-powered competitive intelligence, real-time market insights, and comprehensive analysis of competitor activities to identify opportunities and threats in your industry landscape.
            </p>
            <div class="flex flex-col sm:flex-row gap-4">
              <a href="#contact" class="bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-6 rounded-lg transition duration-300 text-center">
                Request a Demo
              </a>
              <a href="#features" class="bg-transparent hover:bg-white/10 border border-white text-white font-bold py-3 px-6 rounded-lg transition duration-300 text-center">
                Explore Features
              </a>
            </div>
          </div>
          <div class="md:w-1/2">
            <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 shadow-2xl">
              <img src="https://images.unsplash.com/photo-1553484771-371a605b060b?auto=format&fit=crop&q=80&w=1000" 
                   alt="Competitive Edge Dashboard showing market positioning and competitor analysis" class="rounded-lg w-full">
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Key Features Section -->
    <section class="py-16 bg-gray-50" id="features">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold mb-4 text-gray-900">Key Features</h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            Our Competitive Edge platform provides comprehensive tools to monitor competitors, identify market opportunities, and develop effective strategies based on data-driven insights and expert analysis of industry trends.
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          @for (feature of features; track feature.title) {
            <div class="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition duration-300 border border-gray-100">
              <div class="text-purple-600 text-4xl mb-4">
                <i class="fas {{ feature.icon }}"></i>
              </div>
              <h3 class="text-xl font-bold mb-3 text-gray-900">{{ feature.title }}</h3>
              <p class="text-gray-600">{{ feature.description }}</p>
            </div>
          }
        </div>
      </div>
    </section>

    <!-- Competitive Analysis Section -->
    <section class="py-16 bg-white">
      <div class="container mx-auto px-4">
        <div class="flex flex-col md:flex-row items-center">
          <div class="md:w-1/2 mb-10 md:mb-0 md:pr-12">
            <h2 class="text-3xl md:text-4xl font-bold mb-6 text-gray-900">Comprehensive Competitive Analysis</h2>
            <p class="text-xl text-gray-600 mb-6">
              Our AI-powered platform continuously monitors your competitors and market landscape to provide actionable insights.
            </p>
            <ul class="space-y-4">
              <li class="flex items-start">
                <div class="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-1">
                  <i class="fas fa-check text-purple-600 text-sm"></i>
                </div>
                <p class="text-gray-700">Track competitor product launches and pricing strategies</p>
              </li>
              <li class="flex items-start">
                <div class="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-1">
                  <i class="fas fa-check text-purple-600 text-sm"></i>
                </div>
                <p class="text-gray-700">Monitor market share changes and industry trends</p>
              </li>
              <li class="flex items-start">
                <div class="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-1">
                  <i class="fas fa-check text-purple-600 text-sm"></i>
                </div>
                <p class="text-gray-700">Analyze competitor marketing strategies and messaging</p>
              </li>
              <li class="flex items-start">
                <div class="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-1">
                  <i class="fas fa-check text-purple-600 text-sm"></i>
                </div>
                <p class="text-gray-700">Identify emerging market opportunities and threats</p>
              </li>
            </ul>
          </div>
          <div class="md:w-1/2">
            <div class="bg-gray-100 rounded-xl p-6 shadow-lg">
              <img src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=1000" 
                   alt="Competitive Analysis Dashboard showing market share and competitor metrics" class="rounded-lg w-full">
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Strategic Insights Section -->
    <section class="py-16 bg-gray-50">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold mb-4 text-gray-900">Strategic Insights</h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            Transform competitive intelligence into actionable strategies for your business.
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div class="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
            <div class="text-purple-600 text-4xl mb-4">
              <i class="fas fa-chess"></i>
            </div>
            <h3 class="text-xl font-bold mb-3 text-gray-900">Strategic Positioning</h3>
            <p class="text-gray-600 mb-4">
              Identify optimal market positioning based on competitor analysis and market gaps.
            </p>
            <ul class="space-y-2 text-gray-600">
              <li class="flex items-start">
                <i class="fas fa-circle text-xs text-purple-600 mt-1.5 mr-2"></i>
                <span>Differentiation strategy development</span>
              </li>
              <li class="flex items-start">
                <i class="fas fa-circle text-xs text-purple-600 mt-1.5 mr-2"></i>
                <span>Value proposition optimization</span>
              </li>
              <li class="flex items-start">
                <i class="fas fa-circle text-xs text-purple-600 mt-1.5 mr-2"></i>
                <span>Competitive advantage identification</span>
              </li>
            </ul>
          </div>
          
          <div class="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
            <div class="text-purple-600 text-4xl mb-4">
              <i class="fas fa-rocket"></i>
            </div>
            <h3 class="text-xl font-bold mb-3 text-gray-900">Growth Opportunities</h3>
            <p class="text-gray-600 mb-4">
              Discover untapped market segments and expansion opportunities based on competitive analysis.
            </p>
            <ul class="space-y-2 text-gray-600">
              <li class="flex items-start">
                <i class="fas fa-circle text-xs text-purple-600 mt-1.5 mr-2"></i>
                <span>Market gap analysis</span>
              </li>
              <li class="flex items-start">
                <i class="fas fa-circle text-xs text-purple-600 mt-1.5 mr-2"></i>
                <span>Expansion strategy development</span>
              </li>
              <li class="flex items-start">
                <i class="fas fa-circle text-xs text-purple-600 mt-1.5 mr-2"></i>
                <span>New product opportunity identification</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Testimonials Section -->
    <section class="py-16 bg-white">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold mb-4 text-gray-900">What Our Clients Say</h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            Hear from business leaders who have transformed their competitive strategy with our platform.
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          @for (testimonial of testimonials; track testimonial.author) {
            <div class="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
              <div class="text-purple-600 text-4xl mb-4">
                <i class="fas fa-quote-left"></i>
              </div>
              <p class="text-gray-700 mb-6 italic">{{ testimonial.quote }}</p>
              <div class="flex items-center">
                <div class="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold mr-4">
                  {{ testimonial.author.charAt(0) }}
                </div>
                <div>
                  <p class="font-bold text-gray-900">{{ testimonial.author }}</p>
                  <p class="text-gray-600 text-sm">{{ testimonial.position }}, {{ testimonial.company }}</p>
                </div>
              </div>
            </div>
          }
        </div>
      </div>
    </section>

    <!-- FAQ Section -->
    <section class="py-16 bg-gray-50" id="faq">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold mb-4 text-gray-900">Frequently Asked Questions</h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            Get answers to common questions about our Competitive Edge platform.
          </p>
        </div>

        <div class="max-w-4xl mx-auto">
          @for (faq of faqs; track faq.question) {
            <div class="mb-6 border-b border-gray-200 pb-6">
              <h3 class="text-xl font-bold mb-3 text-gray-900">{{ faq.question }}</h3>
              <p class="text-gray-600">{{ faq.answer }}</p>
            </div>
          }
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-16 bg-gradient-to-br from-purple-900 to-blue-900 text-white" id="contact">
      <div class="container mx-auto px-4">
        <div class="max-w-4xl mx-auto text-center">
          <h2 class="text-3xl md:text-4xl font-bold mb-6">Ready to Gain Your Competitive Edge?</h2>
          <p class="text-xl mb-8">
            Join industry leaders who rely on our platform to stay ahead of the competition and drive strategic growth.
          </p>
          <div class="bg-white/10 backdrop-blur-sm rounded-xl p-8 shadow-lg">
            <h3 class="text-2xl font-bold mb-6">Request a Demo</h3>
            <form class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium mb-2">First Name</label>
                <input type="text" class="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 focus:outline-none focus:ring-2 focus:ring-purple-400">
              </div>
              <div>
                <label class="block text-sm font-medium mb-2">Last Name</label>
                <input type="text" class="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 focus:outline-none focus:ring-2 focus:ring-purple-400">
              </div>
              <div>
                <label class="block text-sm font-medium mb-2">Company Email</label>
                <input type="email" class="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 focus:outline-none focus:ring-2 focus:ring-purple-400">
              </div>
              <div>
                <label class="block text-sm font-medium mb-2">Company</label>
                <input type="text" class="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 focus:outline-none focus:ring-2 focus:ring-purple-400">
              </div>
              <div class="md:col-span-2">
                <label class="block text-sm font-medium mb-2">How can we help you?</label>
                <textarea rows="4" class="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 focus:outline-none focus:ring-2 focus:ring-purple-400"></textarea>
              </div>
              <div class="md:col-span-2">
                <button type="submit" class="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-6 rounded-lg transition duration-300">
                  Submit Request
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  `,
  styles: [`
    .bg-pattern {
      background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    }
  `]
})
export class CompetitiveEdgeComponent {
  features: Feature[] = [
    {
      icon: 'fa-binoculars',
      title: 'Competitor Monitoring',
      description: 'Comprehensive tracking of competitor activities, product launches, pricing strategies, marketing campaigns, and business developments through advanced web monitoring and AI analysis.'
    },
    {
      icon: 'fa-chart-line',
      title: 'Market Trend Analysis',
      description: 'AI-powered identification and analysis of emerging market trends, consumer preferences, and industry shifts before they become mainstream, giving you first-mover advantage.'
    },
    {
      icon: 'fa-bullseye',
      title: 'Strategic Opportunity Identification',
      description: 'Systematic identification of market gaps, unmet customer needs, and strategic opportunities based on competitive landscape analysis and predictive modeling.'
    },
    {
      icon: 'fa-balance-scale',
      title: 'Competitive Benchmarking',
      description: 'Detailed comparison of your products, services, and business performance against key competitors across multiple dimensions including quality, pricing, features, and market perception.'
    },
    {
      icon: 'fa-chess',
      title: 'Strategic Planning Support',
      description: 'Decision support tools and scenario planning capabilities to develop effective competitive strategies based on comprehensive market intelligence and competitor analysis.'
    },
    {
      icon: 'fa-tachometer-alt',
      title: 'Performance Metrics',
      description: 'Real-time tracking of your competitive position in the market with customizable KPIs and performance indicators to measure the effectiveness of your strategic initiatives.'
    }
  ];

  testimonials: Testimonial[] = [
    {
      quote: "Competitive Edge has transformed our strategic planning process. We now have real-time insights into competitor movements and market trends that drive our decision-making.",
      author: "Alexandra Richards",
      position: "Chief Strategy Officer",
      company: "Global Innovations Inc."
    },
    {
      quote: "The platform's ability to identify emerging market opportunities has been invaluable. We've launched two new product lines based on insights from Competitive Edge.",
      author: "Marcus Johnson",
      position: "VP of Product Development",
      company: "TechSolutions Group"
    },
    {
      quote: "As a market leader, staying ahead of the competition is critical. Competitive Edge gives us the intelligence we need to maintain our position and anticipate market shifts.",
      author: "Sophia Chen",
      position: "Director of Market Intelligence",
      company: "Premier Brands International"
    }
  ];

  faqs: FAQ[] = [
    {
      question: "How does Competitive Edge gather competitive intelligence?",
      answer: "Our platform uses AI and machine learning to analyze data from thousands of sources, including news articles, press releases, social media, financial reports, and industry publications. We also incorporate proprietary data sources and human intelligence to provide comprehensive competitive insights."
    },
    {
      question: "How frequently is the competitive intelligence updated?",
      answer: "Our platform provides real-time updates and alerts for significant competitive developments. We also deliver weekly summary reports and monthly in-depth analyses to help you track trends and changes over time."
    },
    {
      question: "Can Competitive Edge be customized for our specific industry?",
      answer: "Yes, we tailor the platform to focus on your specific industry, competitors, and areas of interest. We work with you to identify the most relevant data sources and insights for your business needs."
    },
    {
      question: "How does Competitive Edge help with strategic planning?",
      answer: "The platform provides actionable insights and recommendations based on competitive analysis, helping you identify market opportunities, anticipate competitor moves, and develop effective strategic responses."
    },
    {
      question: "Is the platform suitable for small and medium-sized businesses?",
      answer: "Absolutely. We offer scalable solutions that can be tailored to the needs and resources of businesses of all sizes. Our platform is designed to provide valuable competitive intelligence regardless of your company size or market position."
    }
  ];
} 